import { useState, useCallback, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Stethoscope, ClipboardCheck, Check, Circle } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DiagnosticOutcome,
  DiagnosticOutcomeExtended,
  DiagnosticConfidence,
  DiagnosticCaptureData,
  UncapturedDiagnosticJob,
  DIAGNOSTIC_OUTCOME_LABELS,
  DIAGNOSTIC_COMPETENCY_LABELS,
  CONFIDENCE_LABELS,
} from '@/types/diagnostic';

interface DiagnosticPauseCaptureProps {
  uncapturedJobs: UncapturedDiagnosticJob[];
  capturedJobs: DiagnosticCaptureData[];
  skippedJobIds: Set<string>;
  onJobCapture: (data: DiagnosticCaptureData) => void;
  onJobSkip: (jobId: string) => void;
  /** Called when pending form data changes - returns capture data for jobs that are filled out but not yet submitted */
  onPendingDataChange?: (pendingData: DiagnosticCaptureData[]) => void;
}

// Stage D: confidence and "complaint confirmed" start UNANSWERED. They used to
// default to 3 and false, and a filled-in form was auto-submitted with them —
// recording a confidence and a "complaint not confirmed" nobody stated.
interface JobFormState {
  isDiagCompleted: boolean | null;
  outcome: DiagnosticOutcomeExtended | '';
  competencyOutcome: DiagnosticOutcome | '';
  confidence: DiagnosticConfidence | null;
  complaintConfirmed: boolean | null;
}

const isComplete = (f: JobFormState | undefined): f is JobFormState & {
  confidence: DiagnosticConfidence; complaintConfirmed: boolean;
} => f?.isDiagCompleted === true && f.outcome !== '' && f.competencyOutcome !== ''
  && f.confidence !== null && f.complaintConfirmed !== null;

export function DiagnosticPauseCapture({
  uncapturedJobs,
  capturedJobs,
  skippedJobIds,
  onJobCapture,
  onJobSkip,
  onPendingDataChange,
}: DiagnosticPauseCaptureProps) {
  // Track form state for each job
  const [jobFormStates, setJobFormStates] = useState<Record<string, JobFormState>>(() => {
    const initial: Record<string, JobFormState> = {};
    uncapturedJobs.forEach(job => {
      initial[job.jobId] = {
        isDiagCompleted: null,
        outcome: '',
        competencyOutcome: '',
        confidence: null,
        complaintConfirmed: null,
      };
    });
    return initial;
  });

  // Track which job is currently expanded
  const [expandedJobId, setExpandedJobId] = useState<string | null>(
    uncapturedJobs.length === 1 ? uncapturedJobs[0].jobId : null
  );

  const getJobStatus = useCallback((jobId: string): 'pending' | 'captured' | 'skipped' => {
    if (capturedJobs.some(c => c.jobId === jobId)) return 'captured';
    if (skippedJobIds.has(jobId)) return 'skipped';
    return 'pending';
  }, [capturedJobs, skippedJobIds]);

  const addressedCount = uncapturedJobs.filter(
    job => getJobStatus(job.jobId) !== 'pending'
  ).length;

  const handleCompletionChange = useCallback((jobId: string, completed: boolean, job: UncapturedDiagnosticJob) => {
    setJobFormStates(prev => ({
      ...prev,
      [jobId]: { ...prev[jobId], isDiagCompleted: completed },
    }));

    if (!completed) {
      // User said "No" - skip this job
      onJobSkip(jobId);
      setExpandedJobId(null);
      
      // Auto-expand next pending job
      const nextPending = uncapturedJobs.find(
        j => j.jobId !== jobId && getJobStatus(j.jobId) === 'pending'
      );
      if (nextPending) {
        setExpandedJobId(nextPending.jobId);
      }
    } else {
      // User said "Yes" - expand to show form
      setExpandedJobId(jobId);
    }
  }, [uncapturedJobs, onJobSkip, getJobStatus]);

  const handleCompetencyChange = useCallback((jobId: string, competencyOutcome: DiagnosticOutcome) => {
    setJobFormStates((prev) => ({
      ...prev,
      [jobId]: { ...prev[jobId], competencyOutcome },
    }));
  }, []);

  const handleOutcomeChange = useCallback((jobId: string, outcome: DiagnosticOutcomeExtended) => {
    setJobFormStates(prev => ({
      ...prev,
      [jobId]: { ...prev[jobId], outcome },
    }));
  }, []);

  const handleConfidenceChange = useCallback((jobId: string, confidence: DiagnosticConfidence) => {
    setJobFormStates(prev => ({
      ...prev,
      [jobId]: { ...prev[jobId], confidence },
    }));
  }, []);

  const handleComplaintConfirmedChange = useCallback((jobId: string, confirmed: boolean) => {
    setJobFormStates(prev => ({
      ...prev,
      [jobId]: { ...prev[jobId], complaintConfirmed: confirmed },
    }));
  }, []);

  const handleSubmitJob = useCallback((job: UncapturedDiagnosticJob) => {
    const formState = jobFormStates[job.jobId];
    if (!isComplete(formState)) return;

    const captureData: DiagnosticCaptureData = {
      jobId: job.jobId,
      jobName: job.jobName,
      outcome: formState.outcome as DiagnosticOutcomeExtended,
      competencyOutcome: formState.competencyOutcome as DiagnosticOutcome,
      confidence: formState.confidence,
      complaintConfirmed: formState.complaintConfirmed,
      lockType: job.lockType,
    };

    onJobCapture(captureData);
    setExpandedJobId(null);

    // Auto-expand next pending job
    const nextPending = uncapturedJobs.find(
      j => j.jobId !== job.jobId && getJobStatus(j.jobId) === 'pending'
    );
    if (nextPending) {
      setExpandedJobId(nextPending.jobId);
    }
  }, [jobFormStates, onJobCapture, uncapturedJobs, getJobStatus]);

  // Check if a job's form is valid for submission
  const isJobFormValid = useCallback((jobId: string): boolean => isComplete(jobFormStates[jobId]), [jobFormStates]);

  // Report pending data changes to parent - allows parent to auto-capture on "Pause Timer" click
  useEffect(() => {
    if (!onPendingDataChange) return;
    
    const pendingData: DiagnosticCaptureData[] = [];
    for (const job of uncapturedJobs) {
      const formState = jobFormStates[job.jobId];
      const status = getJobStatus(job.jobId);
      
      // Only include jobs that are pending AND fully answered — never a default
      if (status === 'pending' && isComplete(formState)) {
        pendingData.push({
          jobId: job.jobId,
          jobName: job.jobName,
          outcome: formState.outcome as DiagnosticOutcomeExtended,
      competencyOutcome: formState.competencyOutcome as DiagnosticOutcome,
          confidence: formState.confidence,
          complaintConfirmed: formState.complaintConfirmed,
          lockType: job.lockType,
        });
      }
    }
    
    onPendingDataChange(pendingData);
  }, [jobFormStates, uncapturedJobs, getJobStatus, onPendingDataChange]);
  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      {/* Header with progress */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Stethoscope className="h-4 w-4" />
          <span className="text-sm font-medium">
            {/* 8C: "This ticket has..." implied every diagnostic job on the
                ticket belonged to this technician. The list is now scoped to
                the jobs THEIR OWN work episode selected. */}
            Your work includes {uncapturedJobs.length} diagnostic/inspection job{uncapturedJobs.length > 1 ? 's' : ''}
          </span>
        </div>
        <Badge variant={addressedCount === uncapturedJobs.length ? 'default' : 'secondary'}>
          {addressedCount} of {uncapturedJobs.length} addressed
        </Badge>
      </div>

      {/* Job list */}
      <div className="space-y-2">
        {uncapturedJobs.map((job) => {
          const status = getJobStatus(job.jobId);
          const formState = jobFormStates[job.jobId];
          const isExpanded = expandedJobId === job.jobId;
          const isInspection = job.lockType === 'inspection';

          return (
            <Collapsible
              key={job.jobId}
              open={isExpanded && status === 'pending'}
              onOpenChange={(open) => {
                if (status === 'pending') {
                  setExpandedJobId(open ? job.jobId : null);
                }
              }}
            >
              <div className="border rounded-lg">
                <CollapsibleTrigger asChild disabled={status !== 'pending'}>
                  <div
                    className={`flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors ${
                      status !== 'pending' ? 'opacity-70' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {isInspection ? (
                        <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Stethoscope className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="font-medium text-sm">{job.jobName}</span>
                      <Badge variant="outline" className="text-xs">
                        {isInspection ? 'Inspection' : 'Diagnostic'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      {status === 'captured' && (
                        <Badge variant="default" className="gap-1">
                          <Check className="h-3 w-3" />
                          Done
                        </Badge>
                      )}
                      {status === 'skipped' && (
                        <Badge variant="secondary">Not completed</Badge>
                      )}
                      {status === 'pending' && (
                        <Circle className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="p-3 pt-0 space-y-4 border-t">
                    {/* Completion Question */}
                    <div className="space-y-2">
                      <Label>
                        Is this {isInspection ? 'inspection' : 'diagnostic'} completed right now?
                      </Label>
                      <RadioGroup
                        value={formState?.isDiagCompleted === null ? '' : formState?.isDiagCompleted ? 'yes' : 'no'}
                        onValueChange={(v) => handleCompletionChange(job.jobId, v === 'yes', job)}
                        className="flex gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id={`diag-no-${job.jobId}`} />
                          {/* Oct 20: a phone-sized tap target (the Stop dialog requires this answer). */}
                          <Label htmlFor={`diag-no-${job.jobId}`} className="cursor-pointer inline-flex min-h-11 min-w-11 items-center px-2 md:min-h-0 md:min-w-0 md:px-0">
                            No
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id={`diag-yes-${job.jobId}`} />
                          {/* Oct 20: a phone-sized tap target (the Stop dialog requires this answer). */}
                          <Label htmlFor={`diag-yes-${job.jobId}`} className="cursor-pointer inline-flex min-h-11 min-w-11 items-center px-2 md:min-h-0 md:min-w-0 md:px-0">
                            Yes
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Capture Form - only shown when user says "Yes" */}
                    {formState?.isDiagCompleted === true && (
                      <div className="space-y-4 animate-in slide-in-from-top-2 duration-200">
                        {/* Outcome */}
                        <div className="space-y-2">
                          <Label htmlFor={`diag-outcome-${job.jobId}`}>Outcome</Label>
                          <Select
                            value={formState.outcome}
                            onValueChange={(v) => handleOutcomeChange(job.jobId, v as DiagnosticOutcomeExtended)}
                          >
                            <SelectTrigger id={`diag-outcome-${job.jobId}`}>
                              <SelectValue placeholder="Select outcome" />
                            </SelectTrigger>
                            <SelectContent>
                              {(Object.keys(DIAGNOSTIC_OUTCOME_LABELS) as DiagnosticOutcomeExtended[]).map(
                                (key) => (
                                  <SelectItem key={key} value={key}>
                                    {DIAGNOSTIC_OUTCOME_LABELS[key]}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* What the work demonstrated — CORE-I's own fact */}
                        <div className="space-y-2">
                          <Label htmlFor={`diag-competency-${job.jobId}`}>What did the diagnosis find?</Label>
                          <Select
                            value={formState.competencyOutcome}
                            onValueChange={(v) => handleCompetencyChange(job.jobId, v as DiagnosticOutcome)}
                          >
                            <SelectTrigger id={`diag-competency-${job.jobId}`}>
                              <SelectValue placeholder="Select what the diagnosis found" />
                            </SelectTrigger>
                            <SelectContent>
                              {(Object.keys(DIAGNOSTIC_COMPETENCY_LABELS) as DiagnosticOutcome[]).map(
                                (key) => (
                                  <SelectItem key={key} value={key}>
                                    {DIAGNOSTIC_COMPETENCY_LABELS[key]}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Confidence — chosen, never defaulted */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Confidence Level</Label>
                            {formState.confidence !== null && (
                              <Badge variant="secondary" className="font-medium">
                                {formState.confidence} - {CONFIDENCE_LABELS[formState.confidence]}
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-1" role="radiogroup" aria-label="Confidence">
                            {([1, 2, 3, 4, 5] as DiagnosticConfidence[]).map((v) => (
                              <button
                                key={v}
                                type="button"
                                role="radio"
                                aria-checked={formState.confidence === v}
                                data-testid={`diag-confidence-${job.jobId}-${v}`}
                                onClick={() => handleConfidenceChange(job.jobId, v)}
                                className={`flex-1 rounded-md border py-1.5 text-sm ${
                                  formState.confidence === v ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted'
                                }`}
                              >
                                {v}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Complaint Confirmed — chosen, never defaulted */}
                        <div className="space-y-2">
                          <Label>Customer complaint confirmed?</Label>
                          <RadioGroup
                            value={formState.complaintConfirmed === null ? '' : formState.complaintConfirmed ? 'yes' : 'no'}
                            onValueChange={(v) => handleComplaintConfirmedChange(job.jobId, v === 'yes')}
                            className="flex gap-4"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="no" id={`complaint-no-${job.jobId}`} />
                              <Label htmlFor={`complaint-no-${job.jobId}`} className="cursor-pointer">No</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="yes" id={`complaint-yes-${job.jobId}`} />
                              <Label htmlFor={`complaint-yes-${job.jobId}`} className="cursor-pointer">Yes</Label>
                            </div>
                          </RadioGroup>
                        </div>

                        {/* Submit button for this job - always visible but disabled when form incomplete */}
                        <div className="space-y-2">
                          <button
                            type="button"
                            onClick={() => handleSubmitJob(job)}
                            disabled={!isJobFormValid(job.jobId)}
                            className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Confirm {isInspection ? 'Inspection' : 'Diagnostic'}
                          </button>
                          {!isJobFormValid(job.jobId) && (
                            <p className="text-xs text-muted-foreground text-center">
                              Answer each question to record these results
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
      </div>

      {/* Completion hint */}
      {addressedCount === uncapturedJobs.length && (
        <p className="text-xs text-muted-foreground">
          All jobs addressed. Click the button below to continue.
        </p>
      )}
    </div>
  );
}
